import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  createHttpLink,
  from,
  Observable,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Vite uses import.meta.env for environment variables, and they must start with VITE_
const graphqlEndpoint = import.meta.env.VITE_GRAPHQL_ENDPOINT || "/graphql";

// Create HTTP link
const httpLink = createHttpLink({
  uri: graphqlEndpoint,
  credentials: "include",
});

// We rely on cookie-based (httpOnly) auth. The browser will send cookies when
// credentials: 'include' is set on the HTTP link. Do not attempt to read
// httpOnly cookies from JS to set Authorization header.

// Create error link to handle 401 errors and attempt token refresh + retry
const API_BASE = graphqlEndpoint.replace("/graphql", "");

let refreshPromise: Promise<boolean> | null = null;
const refreshTokens = async (): Promise<boolean> => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });

      refreshPromise = null;
      return res.ok;
    } catch (e) {
      refreshPromise = null;
      return false;
    }
  })();

  return refreshPromise;
};

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    const isAuthError = (err: any) => {
      if (!err) return false;
      const status =
        err.statusCode || err.status || (err.extensions && err.extensions.code);
      if (status === 401) return true;
      const msg = String(err.message || "").toLowerCase();
      return msg.includes("unauthorized") || msg.includes("authentication");
    };

    if (graphQLErrors && graphQLErrors.some((e) => isAuthError(e))) {
      // Attempt refresh and retry
      return new Observable((observer) => {
        refreshTokens()
          .then((ok) => {
            if (!ok) {
              window.location.href = "/login";
              observer.error(new Error("Unable to refresh token"));
              return;
            }

            // Retry the failed operation
            const subscriber = {
              next: (result: any) => observer.next(result),
              error: (err: any) => observer.error(err),
              complete: () => observer.complete(),
            };

            forward(operation).subscribe(subscriber);
          })
          .catch((err) => {
            window.location.href = "/login";
            observer.error(err);
          });
      });
    }

    if (networkError && isAuthError(networkError)) {
      return new Observable((observer) => {
        refreshTokens()
          .then((ok) => {
            if (!ok) {
              window.location.href = "/login";
              observer.error(networkError);
              return;
            }

            const subscriber = {
              next: (result: any) => observer.next(result),
              error: (err: any) => observer.error(err),
              complete: () => observer.complete(),
            };

            forward(operation).subscribe(subscriber);
          })
          .catch((err) => {
            window.location.href = "/login";
            observer.error(err);
          });
      });
    }
  },
);

const client = new ApolloClient({
  link: from([errorLink, httpLink]),
  cache: new InMemoryCache(),
});

const theme = createTheme();

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);
root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <ApolloProvider client={client}>
          <App />
        </ApolloProvider>
      </LocalizationProvider>
    </ThemeProvider>
  </React.StrictMode>,
);

reportWebVitals();
